import { csv } from "@hydrologiq/common/transformations/reporting/csv"
import { type RowType, type RowType as RowTypes } from "./rows"

// export const usageValues =

export const usageHeading = (field: string) => {
  switch (field as keyof RowType) {
    case "start":
      return "Start date"
    case "end":
      return "End date"
    case "usedEnergy":
      return "Total energy used (kWh)"
    case "startWeight":
      return "Start weight (kg)"
    case "endWeight":
      return "End weight (kg)"
    case "averageWeight":
      return "Average weight (kg)"
    case "usedWeight":
      return "Weight used (kg)"
    case "averagePower":
      return "Average power (kW)"
    case "peakPower":
      return "Peak power (kW)"
    case "peakPowerTime":
      return "Peak time"
    case "totalEmissions":
      return "CO2e total (kg)"
    case "emissionsFactor":
      return "CO2e (kg/kWh)"
    default:
      return field
  }
}

export const usageFieldExists = (field: string, rows: RowTypes[]) => {
  // A field exists if it's present in at least one row and not null
  // Zero is a valid value (e.g., no emissions, no consumption in a period)
  return rows.some((row) => {
    const value = row[field as keyof RowType]
    // Field must be defined and not null
    // Always include 'start' and 'end' regardless of value
    if (field === "start" || field === "end") {
      return value !== undefined
    }
    return value !== undefined && value !== null
  })
}

export const usageFieldOrder = (row: RowType) => {
  const orderedFields = [
    "start",
    "end",
    "usedEnergy",
    "averagePower",
    "peakPower",
    "peakPowerTime",
    "averageWeight",
    "startWeight",
    "endWeight",
    "usedWeight",
    "totalEmissions",
    "emissionsFactor",
  ]
  return Object.keys(row).sort((a, b) => orderedFields.indexOf(a) - orderedFields.indexOf(b))
}

export const usageFields = (rows: RowTypes[], filterFields?: (keyof RowType)[]): (keyof RowType)[] => {
  if (rows.length === 0) {
    return []
  }

  let fields = usageFieldOrder(rows[0]).filter(
    (field) => field !== "__typename" && usageFieldExists(field, rows),
  ) as (keyof RowType)[]

  // Apply field filter if provided
  if (filterFields && filterFields.length > 0) {
    // Maintain the ordering but only include requested fields
    fields = fields.filter((field) => filterFields.includes(field))
  }

  return fields
}

export const usageCsv = (rows: RowTypes[], filterFields?: (keyof RowType)[]) => {
  const fields = usageFields(rows, filterFields)

  let headings = fields.map((field) => usageHeading(field))

  const values = rows.map((value) => {
    return fields.map((field) => value[field as keyof RowType] ?? 0)
  })

  return csv([headings, ...values])
}

/**
 * Get all available fields from data rows
 * This returns all fields that exist in the data, useful for field selection UI
 */
export const getAvailableFields = (rows: RowTypes[]): (keyof RowType)[] => {
  return usageFields(rows)
}
